import {
  CheckBadgeIcon,
  ClockIcon,
  InboxArrowDownIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

export type Order = {
  id: string;
  title: string;
  trackingNumber: string | null;
  orderId: string;
  orderDate: string;
  status: OrderStepValue;
};

export type OrderDetailStep = {
  step: OrderStepValue;
  timestamp?: string;
};

type OrderStep = {
  value: string | null;
  label: string;
  icon: typeof ClockIcon | undefined;
  className: string | undefined;
  note: string | undefined;
};

export const orderSteps = [
  {
    value: null,
    label: "All statuses",
    icon: undefined,
    className: undefined,
    note: undefined,
  },
  {
    value: "USER_SHIPPED",
    label: "User Shipped",
    icon: TruckIcon,
    className: "border-gray-300 text-gray-500",
    note: "Ship your item to start tracking your order progress.",
  },
  {
    value: "SELLER_RECEIVED",
    label: "Seller Received",
    icon: InboxArrowDownIcon,
    className: "border-gray-300 text-gray-500",
    note: "Item has been received by our technical team.",
  },
  {
    value: "IN_REPAIR",
    label: "In Repair",
    icon: WrenchScrewdriverIcon,
    className: "border-blue-200 text-blue-600",
    note: "Your item is currently being repaired.",
  },
  {
    value: "REPAIR_COMPLETE",
    label: "Repair Complete",
    icon: CheckBadgeIcon,
    className: "border-green-200 text-green-600",
    note: "Repair has been successfully finished.",
  },
  {
    value: "SELLER_SHIPPED",
    label: "Seller Shipped",
    icon: TruckIcon,
    className: "border-[#F9814D]/40 text-[#F9814D]",
    note: "Package is ready and shipped for return.",
  },
  {
    value: "USER_RECEIVED",
    label: "User Received",
    icon: CheckBadgeIcon,
    className: "border-green-200 text-green-600",
    note: "Order process has been successfully completed.",
  },
] as const satisfies readonly OrderStep[];

export type OrderStepValue = Exclude<
  (typeof orderSteps)[number]["value"],
  null
>;

export const getOrderStatusOption = (value: OrderStepValue | null) => {
  const option = orderSteps.find((o) => o.value === value);
  if (!option) {
    throw new Error(`Invalid order status: ${value}`);
  }
  return option;
};

export const getCurrentStatus = (steps: OrderDetailStep[]) => {
  const currentIndex = steps.filter((s) => s.timestamp).length - 1;
  const currentStepData = steps[currentIndex];
  const activeOption = currentStepData
    ? getOrderStatusOption(currentStepData.step)
    : null;

  return {
    currentIndex,
    note: activeOption?.note || null,
  };
};
